import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrashCan,
  faArrowLeft,
  faArrowRightFromBracket,
  faEdit,
  faBars,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { Button, Modal } from "react-bootstrap";
import Toast from "../../Components/Toast";
import styles from "../../Styles/Knowledge Center/CategoryDashboard.module.css";
import gearsGif from "../../Assets/gears.gif";

export const CategoryDashboard = () => {
  const { mainCategoryId } = useParams();
  const navigate = useNavigate();

  // State management
  const [mainCategory, setMainCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);
  const [topics, setTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [showAddTopicModal, setShowAddTopicModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [topicName, setTopicName] = useState("");
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [addingTopic, setAddingTopic] = useState(false);
  
  // Mobile menu state
  const [showMobileTopics, setShowMobileTopics] = useState(false);
  const fetchMainCategoryDataRef = useRef(null);

  const activeSubCategory = subCategories.find((s) => s._id === selectedSubCategoryId);

  // Fetch main category and subcategories
  const fetchMainCategoryData = async () => {
    try {
      setLoading(true);
      const token = Cookies.get("adminToken");

      // Fetch subcategories first
      const subCatRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/sub-categories/${mainCategoryId}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      const subs = subCatRes.data?.data || [];
      setSubCategories(subs);

      // Try to get main category name from the first subcategory or set a default
      // Since we don't have a direct admin API for single main category
      const allCatsRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/main-categories`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      
      const allCats = allCatsRes.data?.data || [];
      const currentCat = allCats.find(cat => cat._id === mainCategoryId);
      setMainCategory(currentCat || { name: "Category", _id: mainCategoryId });

      if (subs.length > 0) {
        setSelectedSubCategoryId(subs[0]._id);
        fetchTopicsForSubCategory(subs[0]._id);
      }
    } catch (error) {
      console.error("Error fetching main category:", error);
      toast.error("Failed to load category data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch topics for a subcategory
  const fetchTopicsForSubCategory = async (subCategoryId) => {
    try {
      setLoading(true);
      const token = Cookies.get("adminToken");

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/topics/${mainCategoryId}/${subCategoryId}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      const topicsList = response.data?.data?.topics || [];
      setTopics(topicsList);

      // Auto-select first topic
      if (topicsList.length > 0) {
        handleTopicSelect(topicsList[0]._id, subCategoryId);
      } else {
        setSelectedTopicId(null);
        setEntries([]);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
      toast.error("Failed to load topics");
      setTopics([]);
      setSelectedTopicId(null);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch entries for a topic
  const handleTopicSelect = async (topicId, subCategoryId) => {
    try {
      setSelectedTopicId(topicId);
      setLoading(true);
      const token = Cookies.get("adminToken");

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/entries/${mainCategoryId}/${subCategoryId}/${topicId}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      const list = response.data?.data?.entries || [];
      const groupedMap = new Map();

      list.forEach((entry) => {
        const key = entry.title || "Untitled";
        const statusValue =
          typeof entry.status === "string" && entry.status.trim()
            ? entry.status.trim().toLowerCase()
            : "published";
        if (!groupedMap.has(key)) {
          groupedMap.set(key, {
            title: key,
            createdAt: entry.createdAt,
            representativeEntryId: entry._id,
            status: statusValue === "draft" ? "draft" : "published",
          });
        } else {
          const existing = groupedMap.get(key);
          if (statusValue === "draft") {
            existing.status = "draft";
          }
          if (
            entry.createdAt &&
            (!existing.createdAt ||
              new Date(entry.createdAt) < new Date(existing.createdAt))
          ) {
            existing.createdAt = entry.createdAt;
            existing.representativeEntryId = entry._id;
          }
        }
      });

      setEntries(Array.from(groupedMap.values()));
      // Close mobile menu after selection
      setShowMobileTopics(false);
    } catch (error) {
      console.error("Error fetching entries:", error);
      toast.error("Failed to load entries");
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle subcategory change
  const handleSubCategoryChange = (subCategoryId) => {
    setSelectedSubCategoryId(subCategoryId);
    fetchTopicsForSubCategory(subCategoryId);
  };

  // Add topic
  const handleAddTopic = async () => {
    if (!topicName.trim()) {
      toast.error("Topic name is required");
      return;
    }

    if (!activeSubCategory) {
      toast.error("No subcategory selected");
      return;
    }

    try {
      setAddingTopic(true);
      const token = Cookies.get("adminToken");
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/topic/${mainCategoryId}/${activeSubCategory._id}`,
        { name: topicName.trim() },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      toast.success("Topic added successfully");
      setShowAddTopicModal(false);
      setTopicName("");
      fetchTopicsForSubCategory(activeSubCategory._id);
    } catch (error) {
      console.error("Error adding topic:", error);
      toast.error("Failed to add topic");
    } finally {
      setAddingTopic(false);
    }
  };

  // Delete entry
  const handleDeleteEntry = async () => {
    if (!entryToDelete || !selectedTopicId || !activeSubCategory) return;

    try {
      setLoading(true);
      const token = Cookies.get("adminToken");
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/admin/entry/${mainCategoryId}/${activeSubCategory._id}/${selectedTopicId}/${entryToDelete.representativeEntryId}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      toast.success("Entry deleted successfully");
      setShowDeleteModal(false);
      setEntryToDelete(null);
      handleTopicSelect(selectedTopicId, activeSubCategory._id);
    } catch (error) {
      console.error("Error deleting entry:", error);
      toast.error("Failed to delete entry");
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    Cookies.remove("adminToken");
    navigate("/admin/signin");
  };

  fetchMainCategoryDataRef.current = fetchMainCategoryData;

  useEffect(() => {
    if (mainCategoryId) {
      fetchMainCategoryDataRef.current?.();
    }
  }, [mainCategoryId]);

  return (
    <>
      <Toast />
      <div className={styles.pageContainer}>
        {/* Header with Subcategories */}
        <header className={styles.simpleHeader}>
          <div className={styles.headerTop}>
            <div className={styles.headerLeft}>
              <button
                className={styles.mobileMenuBtn}
                onClick={() => setShowMobileTopics(!showMobileTopics)}
                title="Toggle Topics"
              >
                <FontAwesomeIcon icon={faBars} />
                <span>Topics</span>
              </button>
              <button
                className={styles.backHomeBtn}
                onClick={() => navigate("/admin/dashboard")}
                title="Back to Home"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                <span>Back</span>
              </button>
            </div>
            <div className={styles.headerCenter}>
              <h1 className={styles.categoryTitle}>{mainCategory?.name || "Category"}</h1>
            </div>
            <div className={styles.headerRight}>
              <button
                className={styles.logoutBtn}
                onClick={handleLogout}
                title="Logout"
              >
                <FontAwesomeIcon icon={faArrowRightFromBracket} />
                <span>Logout</span>
              </button>
            </div>
          </div>
          {/* Subcategories in bottom row */}
          <div className={styles.headerBottom}>
            <div className={styles.subcategoriesInNav}>
              {subCategories.map((subCat) => (
                <button
                  key={subCat._id}
                  className={`${styles.subcategoryBtn} ${
                    selectedSubCategoryId === subCat._id
                      ? styles.subcategoryBtnActive
                      : ""
                  }`}
                  onClick={() => handleSubCategoryChange(subCat._id)}
                >
                  {subCat.name}
                </button>
              ))}
            </div>
          </div>
        </header>

        {/* Main Content - 2 Panel Layout */}
        <div className={styles.mainContentArea}>
          {/* Left Panel - Topics */}
          <div className={`${styles.topicsPanel} ${showMobileTopics ? styles.topicsPanelOpen : ''}`}>
            <div className={styles.panelHeader}>
              <h3 className={styles.panelTitle}>Topics</h3>
              <button
                className={styles.addTopicBtn}
                onClick={() => setShowAddTopicModal(true)}
                title="Add Topic"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Add Topic</span>
              </button>
              <button 
                className={styles.closeMobileTopics}
                onClick={() => setShowMobileTopics(false)}
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className={styles.topicsListContainer}>
              {loading && topics.length === 0 ? (
                <div className={styles.loadingState}>
                  <img src={gearsGif} alt="Loading" width="30" />
                  <p>Loading topics...</p>
                </div>
              ) : topics.length > 0 ? (
                topics.map((topic) => (
                  <div
                    key={topic._id}
                    className={`${styles.topicCard} ${
                      selectedTopicId === topic._id
                        ? styles.topicCardActive
                        : ""
                    }`}
                    onClick={() =>
                      handleTopicSelect(topic._id, selectedSubCategoryId)
                    }
                  >
                    <span className={styles.topicName}>{topic.name}</span>
                    <span className={styles.topicArrow}>→</span>
                  </div>
                ))
              ) : (
                <div className={styles.emptyState}>
                  <p>No topics available.</p>
                  <p className={styles.emptySubtext}>Click "Add Topic" to create one.</p>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Overlay */}
          {showMobileTopics && (
            <div 
              className={styles.mobileOverlay}
              onClick={() => setShowMobileTopics(false)}
            />
          )}

          {/* Right Panel - Blogs/Entries */}
          <div className={styles.blogsPanel}>
            <div className={styles.panelHeader}>
              {selectedTopicId && activeSubCategory && (
                <button
                  className={styles.addEntryBtn}
                  onClick={() =>
                    navigate(
                      `/admin/topic-entries/${mainCategoryId}/${activeSubCategory._id}/${selectedTopicId}/create`
                    )
                  }
                  title="Add Entry"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add Entry
                </button>
              )}
            </div>
            <div className={styles.entriesListContainer}>
              {loading && entries.length === 0 ? (
                <div className={styles.loadingState}>
                  <img src={gearsGif} alt="Loading" width="30" />
                  <p>Loading entries...</p>
                </div>
              ) : entries.length > 0 ? (
                entries.map((entry, index) => (
                  <div
                    key={entry.representativeEntryId || index}
                    className={styles.blogCard}
                  >
                    <div 
                      className={styles.blogInfo}
                      onClick={() => {
                        const viewPath = entry.status === "draft" 
                          ? `/admin/topic-entries/${mainCategoryId}/${activeSubCategory._id}/${selectedTopicId}/preview/${entry.representativeEntryId}`
                          : `/admin/topic-entries/${mainCategoryId}/${activeSubCategory._id}/${selectedTopicId}/view/${entry.representativeEntryId}`;
                        navigate(viewPath);
                      }}
                      style={{ cursor: 'pointer' }}
                      title={entry.status === "draft" ? "Preview draft entry" : "View published entry"}
                    >
                      <span className={styles.blogTitle}>{entry.title}</span>
                      {entry.status === "draft" && (
                        <span className={styles.draftBadge}>Draft</span>
                      )}
                    </div>
                    <div className={styles.blogActions}>
                      {entry.status === "draft" && (
                        <button
                          className={styles.editBtn}
                          onClick={() =>
                            navigate(
                              `/admin/topic-entries/${mainCategoryId}/${activeSubCategory._id}/${selectedTopicId}/edit/${entry.representativeEntryId}`
                            )
                          }
                          title="Edit draft entry"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                      )}
                      <button
                        className={styles.removeBlogBtn}
                        onClick={() => {
                          setEntryToDelete(entry);
                          setShowDeleteModal(true);
                        }}
                        title="Remove entry"
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </button>
                    </div>
                  </div>
                ))
              ) : selectedTopicId ? (
                <div className={styles.emptyState}>
                  <p>No entries available for this topic.</p>
                  <p className={styles.emptySubtext}>Click "Add Entry" to create one.</p>
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <p>Select a topic to view entries.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Topic Modal */}
      <Modal
        show={showAddTopicModal}
        onHide={() => setShowAddTopicModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Topic</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label>Topic Name</label>
            <input
              type="text"
              className="form-control"
              value={topicName}
              onChange={(e) => setTopicName(e.target.value)}
              placeholder="Enter topic name"
              autoComplete="off"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAddTopicModal(false)}
            disabled={addingTopic}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddTopic}
            disabled={addingTopic}
          >
            {addingTopic ? "Creating..." : "Create Topic"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Entry Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Entry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this entry?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteEntry}
            disabled={loading}
          >
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
